import React, { useEffect, useMemo } from "react";
import { Box, Link, Typography } from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { CONSTANTS } from "../../../common/utility/constant";
import usePermissions from "../../../common/hooks/usePermissions";
import { useWindowSize } from "../../../common/hooks/useWindowSize";

const FREE_USER_DELAY_STRIP_ROUTES = new Set([
    "/market-profile",
    "/options-table",
    "/greek-chart",
]);

const IV_OI_BAR_ROUTE_PATTERNS = [
    /\/bar-chart\/.*_Strike_IV$/i,
    /\/bar-chart\/.*_Strike_OI$/i,
    /\/bar-chart\/.*_Strike_Wcash$/i,
];

const IV_OI_LINE_ROUTE_PATTERNS = [
    /\/line-chart\/.*_Options_OI_Change_Charts$/i,
];

const VOL_CHART_ROUTE_PATTERNS = [
    /\/line-chart\/.*_Options_Vol_Charts$/i,
];

const FREE_USER_DELAY_STRIP_ROUTE_PATTERNS = [
    ...IV_OI_BAR_ROUTE_PATTERNS,
    ...IV_OI_LINE_ROUTE_PATTERNS,
    ...VOL_CHART_ROUTE_PATTERNS,
];

/** Theme breakpoints: tablet 640, laptop 1024. */
const TABLET_BREAKPOINT = 640;
const LAPTOP_BREAKPOINT = 1024;

const SIZE_STYLES = {
    large: {
        px: 2.75,
        py: 1.15,
        gap: 1,
        icon: 22,
        primaryFont: "0.9375rem",
        secondaryFont: "0.875rem",
        borderRadius: "26px",
    },
    xlarge: {
        px: 3.5,
        py: 1.4,
        gap: 1.15,
        icon: 26,
        primaryFont: "1.0625rem",
        secondaryFont: "0.9375rem",
        borderRadius: "30px",
        borderWidth: "2px",
    },
};

const STATIC_BANNER_POSITION = {
    width: "100%",
    px: 1.5,
    py: 0.75,
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
};

const EMBEDDED_BANNER_POSITION = {
    width: "100%",
    px: 1.5,
    py: 0.5,
    my: 0.5,
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
};

const centeredTop = (offset) => ({
    top: offset,
    left: "50%",
    transform: "translateX(-50%)",
    width: "max-content",
    maxWidth: "calc(100vw - 24px)",
});

export const isFreeUserDelayStripRoute = (pathname = "") => {
    const normalized = pathname.replace(/\/+$/, "") || "/";
    if (FREE_USER_DELAY_STRIP_ROUTES.has(normalized)) return true;
    return FREE_USER_DELAY_STRIP_ROUTE_PATTERNS.some((p) => p.test(normalized));
};

const matchesRoutePatterns = (pathname = "", patterns = []) =>
    patterns.some((p) => p.test(pathname.replace(/\/+$/, "") || "/"));

const isChartRoute = (pathname) =>
    matchesRoutePatterns(pathname, IV_OI_BAR_ROUTE_PATTERNS) ||
    matchesRoutePatterns(pathname, IV_OI_LINE_ROUTE_PATTERNS) ||
    matchesRoutePatterns(pathname, VOL_CHART_ROUTE_PATTERNS);

const STATIC_BANNER_LAYOUT = {
    positionMode: "static",
    position: STATIC_BANNER_POSITION,
    useBannerStyle: true,
};

/** Max width for centred in-flow banner pills (charts + options table). */
const BANNER_PILL_MAX_WIDTH = "min(1200px, calc(100vw - 24px))";

const isCompactViewport = (viewportWidth) =>
    viewportWidth !== undefined && viewportWidth < LAPTOP_BREAKPOINT;

/**
 * IV / OI / Vol charts: in-flow top banner.
 * Other compact routes: in-flow banner. Desktop: fixed overlay.
 * (Options table is handled separately via the `embedded` prop, not this function.)
 */
const getLayout = (pathname, viewportWidth) => {
    const normalized = pathname.replace(/\/+$/, "") || "/";
    const bottomOffset = CONSTANTS.FIXED_FOOTER_HEIGHT + 28;
    const chartRoute = isChartRoute(pathname);
    const size = chartRoute || normalized === "/market-profile" ? "xlarge" : "large";

    if (chartRoute) {
        return { size, ...STATIC_BANNER_LAYOUT };
    }

    if (isCompactViewport(viewportWidth)) {
        return { size, ...STATIC_BANNER_LAYOUT };
    }

    if (normalized === "/market-profile") {
        return {
            size,
            positionMode: "fixed",
            position: {
                bottom: bottomOffset,
                left: 32,
                right: "auto",
                transform: "none",
                width: "max-content",
                maxWidth: "calc(100vw - 24px)",
            },
        };
    }

    return {
        size,
        positionMode: "fixed",
        position: centeredTop(CONSTANTS.DASHBOARD_HEADER_HEIGHT + 8),
    };
};

export const getDashboardContentTopOffset = () =>
    CONSTANTS.DASHBOARD_HEADER_HEIGHT;

const getPillSx = (size, useBannerStyle, isNarrow) => {
    if (useBannerStyle) {
        return {
            px: isNarrow ? 1.25 : size.px,
            py: isNarrow ? 0.75 : size.py,
            gap: isNarrow ? 0.5 : size.gap,
            borderRadius: isNarrow ? "12px" : size.borderRadius,
            flexDirection: isNarrow ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            whiteSpace: isNarrow ? "normal" : "nowrap",
            width: "max-content",
            minWidth: isNarrow ? "auto" : "480px",
            maxWidth: isNarrow ? "calc(100vw - 24px)" : BANNER_PILL_MAX_WIDTH,
        };
    }

    return {
        px: isNarrow ? 1.25 : size.px,
        py: isNarrow ? 0.75 : size.py,
        gap: isNarrow ? 0.5 : size.gap,
        borderRadius: isNarrow ? "12px" : size.borderRadius,
        flexDirection: isNarrow ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        whiteSpace: isNarrow ? "normal" : "nowrap",
        width: isNarrow ? "100%" : "auto",
        maxWidth: isNarrow ? "100%" : "none",
    };
};

function DelayStripPill({ size, isNarrow, useBannerStyle }) {
    const showSeparator = useBannerStyle ? !isNarrow : true;

    return (
        <Box
            sx={{
                pointerEvents: "auto",
                display: "flex",
                ...getPillSx(size, useBannerStyle, isNarrow),
                background:
                    "linear-gradient(145deg, #fde68a 0%, #fbbf24 50%, #f59e0b 100%)",
                border: `${size.borderWidth ?? "1.5px"} solid #d97706`,
                boxShadow:
                    useBannerStyle && isNarrow
                        ? "none"
                        : "0 6px 20px rgba(180, 83, 9, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.45)",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: isNarrow ? 0.5 : size.gap,
                    justifyContent: "center",
                }}
            >
                <ScheduleOutlinedIcon
                    sx={{
                        fontSize: isNarrow ? 18 : size.icon,
                        color: "#92400e",
                        flexShrink: 0,
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        fontSize: isNarrow ? "0.8rem" : size.primaryFont,
                        fontWeight: 700,
                        color: "#451a03",
                        lineHeight: 1.25,
                    }}
                >
                    15-minute delayed data
                </Typography>
            </Box>
            {showSeparator && (
                <Typography
                    component="span"
                    sx={{
                        fontSize: size.secondaryFont,
                        fontWeight: 500,
                        color: "#78350f",
                        lineHeight: 1,
                    }}
                >
                    ·
                </Typography>
            )}
            <Typography
                component="span"
                sx={{
                    fontSize: isNarrow ? "0.75rem" : size.secondaryFont,
                    fontWeight: 600,
                    color: "#78350f",
                    lineHeight: 1.25,
                }}
            >
                <Link
                    component={RouterLink}
                    to="/subscription"
                    underline="always"
                    sx={{
                        fontWeight: 700,
                        color: "#451a03",
                        textDecorationColor: "rgba(69, 26, 3, 0.45)",
                        "&:hover": {
                            color: "#1c0a00",
                            textDecorationColor: "#1c0a00",
                        },
                    }}
                >
                    Upgrade now
                </Link>
                {" for live data feed!"}
            </Typography>
        </Box>
    );
}

/**
 * Single entry point.
 * - Default usage `<FreeUserDelayStrip />`: route-driven fixed/static banner
 *   (mount once, e.g. in the dashboard shell). Skips options-table, since
 *   that route renders its own inline placement instead.
 * - `<FreeUserDelayStrip embedded />`: drop directly in JSX at a specific
 *   spot (e.g. inside the options table, between metadata and the grid).
 */
export default function FreeUserDelayStrip({ embedded = false }) {
    const { pathname } = useLocation();
    const permissions = usePermissions();
    const { width: viewportWidth } = useWindowSize();
    const isFreeUser = permissions.isFreeUser();
    const isNarrow =
        viewportWidth !== undefined && viewportWidth < TABLET_BREAKPOINT;

    const normalized = pathname.replace(/\/+$/, "") || "/";
    const visible =
        isFreeUser &&
        isFreeUserDelayStripRoute(pathname) &&
        normalized !== "/options-table";
    const layout = useMemo(
        () => getLayout(pathname, viewportWidth),
        [pathname, viewportWidth],
    );

    useEffect(() => {
        if (embedded) return;
        document.documentElement.style.setProperty(
            "--dashboard-content-top",
            `${CONSTANTS.DASHBOARD_HEADER_HEIGHT}px`,
        );

        return () => {
            document.documentElement.style.removeProperty(
                "--dashboard-content-top",
            );
        };
    }, [embedded]);


    if (embedded) {
        if (!isFreeUser) return null;
        return (
            <Box role="status" aria-live="polite" sx={EMBEDDED_BANNER_POSITION}>
                <DelayStripPill size={SIZE_STYLES.large} isNarrow={isNarrow} useBannerStyle />
            </Box>
        );
    }

    if (!visible || !layout) return null;

    const useBannerStyle = layout.useBannerStyle === true;
    const size = SIZE_STYLES[layout.size];

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: layout.positionMode,
                zIndex: useBannerStyle ? "auto" : 1100,
                pointerEvents: "none",
                ...layout.position,
            }}
        >
            <DelayStripPill size={size} isNarrow={isNarrow} useBannerStyle={useBannerStyle} />
        </Box>
    );
}